import { ColliderShape, type Collider } from '../components/Collider';
import { type WorldTransform } from '../entities/WorldTransform';
import { rotatedX, rotatedY } from '../math/MathUtils';
import { Rect } from '../math/Rect';

/**
 * Where a {@link Collider} ends up in the world once its entity's transform is folded in.
 *
 * The collider counterpart of `SpriteQuad`, and shared for the same reason: `CollisionSystem` and
 * `DebugSystem` must agree on exactly where a collider is, or the outline drawn over a scene would sit
 * somewhere other than the shape that is actually being tested.
 *
 * Fields are mutable because callers refill one instance rather than allocating per collider per frame.
 */
export interface WorldColliderShape {
  /** Which shape to test: the circle fields or the box bounds. */
  shape: ColliderShape;
  /** World X of the center, offset included. */
  x: number;
  /** World Y of the center, offset included. */
  y: number;
  /** Scaled radius, used when the shape is a circle. */
  radius: number;
  /** Scaled axis aligned bounds, used when the shape is a box. */
  readonly bounds: Rect;
}

/** An unfilled {@link WorldColliderShape}, for a caller that needs one per collider. */
export function createWorldColliderShape(): WorldColliderShape {
  return { shape: ColliderShape.Circle, x: 0, y: 0, radius: 0, bounds: new Rect() };
}

/**
 * Fills `out` with the world shape a collider occupies, and returns it.
 *
 * The offset rides the entity's rotation and scale, but the shape itself does not: a box stays axis
 * aligned and a circle takes the larger of the two scales, so a squashed entity still collides with
 * everything its art covers.
 */
export function fillWorldCollider(
  out: WorldColliderShape,
  world: WorldTransform,
  collider: Collider,
): WorldColliderShape {
  const cos = Math.cos(world.rotation);
  const sin = Math.sin(world.rotation);
  const offsetX = collider.offset.x * world.scale.x;
  const offsetY = collider.offset.y * world.scale.y;
  const scaleX = Math.abs(world.scale.x);
  const scaleY = Math.abs(world.scale.y);

  out.shape = collider.shape;
  out.x = world.position.x + rotatedX(offsetX, offsetY, cos, sin);
  out.y = world.position.y + rotatedY(offsetX, offsetY, cos, sin);
  out.radius = collider.radius * Math.max(scaleX, scaleY);
  out.bounds.set(0, 0, collider.size.x * scaleX, collider.size.y * scaleY).setCenter(out.x, out.y);
  return out;
}
